import { apiService } from './api';

/**
 * Appels de la console super-admin (blueprint `companies`, préfixe /api/admin/companies).
 *
 * Garde serveur : `super_admin_required()` relit le rôle EN BASE et est STRICT —
 * un ADMIN reçoit 403 (« Accès refusé. Rôle super-admin requis. »), pas 401. Le front
 * doit donc distinguer les deux : 401 = session perdue, 403 = rôle insuffisant.
 *
 * Service séparé de companyService.js volontairement : celui-ci répond à « MON
 * entreprise » (garde ADMIN ou SUPER_ADMIN), celui-là à « TOUTES les entreprises »
 * (garde SUPER_ADMIN stricte). Les fusionner mélangerait deux périmètres d'autorisation.
 */

/**
 * Liste des entreprises + usage.
 *
 * ⚠️ La réponse APLATIT `nb_stocks`, `nb_users` et `nb_admins` dans CHAQUE entreprise :
 *     { companies: [ { id, name, plan, status, max_stocks, ..., nb_stocks, nb_users, nb_admins } ] }
 * alors que GET /admin/company (companyService.getMyCompany) les IMBRIQUE sous `usage` :
 *     { company: {...}, usage: { nb_stocks, nb_users } }
 * Les valeurs viennent de requêtes SQL identiques, mais les DEUX CONTRATS NE SONT PAS
 * INTERCHANGEABLES. Passer par les adaptateurs de utils/quotas.js plutôt que de lire
 * les champs à la main.
 *
 * Pas de pagination ni de tri paramétrable côté serveur : `order_by(Company.id)` en dur,
 * tout est renvoyé.
 */
export const getCompanies = async () => {
  const response = await apiService.get('/admin/companies');
  return response.data;
};

/**
 * Détail d'une entreprise : to_dict() + nb_stocks/nb_users/nb_admins APLATIS, plus `stocks` et
 * `users` (avec leur `role`). Réponse : { company: {...} }.
 * 404 si l'entreprise n'existe pas.
 */
export const getCompany = async (companyId) => {
  const response = await apiService.get(`/admin/companies/${companyId}`);
  return response.data;
};

/**
 * Création. Payload : { name, plan?, max_stocks?, users_per_stock? }. Réponse 201 :
 * { company: to_dict() }.
 *
 * ⚠️ `status` N'EST PAS LU : le serveur force 'active' à la création, quel que soit le
 * payload. Une entreprise ne peut pas naître suspendue.
 * ⚠️ AUCUN UTILISATEUR n'est créé : l'entreprise naît vide, donc sans ADMIN, donc
 * inutilisable telle quelle. Comment elle obtient son premier ADMIN est une question
 * ouverte, notée au ROADMAP.
 *
 * 400 : nom vide, nom déjà pris, plan invalide (+ `valid_plans`), override non entier >= 1.
 */
export const createCompany = async (payload) => {
  const response = await apiService.post('/admin/companies', payload);
  return response.data;
};

/**
 * Mise à jour PARTIELLE : une clé absente du payload reste inchangée.
 *
 * 🔴 PIÈGE N°1 — LE NOM DU CHAMP D'ÉCRITURE N'EST PAS CELUI DE LECTURE.
 * En SORTIE, `to_dict()` expose DEUX champs :
 *     max_stocks           = limite EFFECTIVE (override ?? plan), lecture seule
 *     max_stocks_override  = l'override brut, null si l'entreprise suit son plan
 * En ENTRÉE, ce PUT écrit l'OVERRIDE sous la clé `max_stocks` (companies.py:151-159,
 * `setattr(company, 'max_stocks', val)` vise la COLONNE, qui est l'override).
 *     -> envoyer `max_stocks`          MODIFIE l'override
 *     -> envoyer `max_stocks_override` est une CLÉ INCONNUE : ignorée EN SILENCE, 200
 *        renvoyé, rien de changé. Aucune erreur ne le signalera.
 * Le formulaire doit donc PRÉ-REMPLIR depuis `*_override` et ENVOYER sous `max_stocks`.
 * Pré-remplir depuis `max_stocks` figerait en override une valeur héritée du plan :
 * l'entreprise cesserait de suivre les changements de plan, sans rien à l'écran.
 * Idem pour `users_per_stock`.
 *
 * Un override à `null` est un RETRAIT explicite (retour aux limites du plan) — à
 * distinguer de la clé absente, qui laisse inchangé.
 *
 * 400 : nom vide, nom déjà pris, plan invalide, statut hors (active/suspended),
 * override non entier >= 1 et non null. 404 si l'entreprise n'existe pas.
 */
export const updateCompany = async (companyId, payload) => {
  const response = await apiService.put(`/admin/companies/${companyId}`, payload);
  return response.data;
};

/**
 * Suppression. Réponse 200 : { message: 'Entreprise supprimée' }.
 *
 * ⚠️ AUCUNE CASCADE, JAMAIS. Le serveur refuse en **409** si l'entreprise a encore
 * des stocks OU des utilisateurs (l'un des deux suffit), et renvoie :
 *     { error: "…encore N stock(s) et M utilisateur(s). Videz-la d'abord (ou
 *               suspendez-la).", nb_stocks: N, nb_users: M }
 * Le message interpole les compteurs RÉELS et suggère déjà la suspension : il doit être
 * affiché tel quel, jamais reformulé côté front.
 *
 * Le 409 est un refus MÉTIER, à distinguer des autres codes : 403 (rôle insuffisant),
 * 404 (entreprise inexistante), 500. Seul le 409 justifie de proposer une alternative.
 */
export const deleteCompany = async (companyId, confirmation) => {
  // 🔴 LA CONFIRMATION VOYAGE DANS LE CORPS, ET ELLE EST OBLIGATOIRE. Le
  // serveur refuse en 400 si elle ne correspond pas EXACTEMENT à la raison
  // sociale — casse comprise. *Ce n'est pas un contrôle de formulaire : c'est
  // la seconde des trois barrières*, après le rôle et avant l'atomicité.
  // ⚠️ `axios.delete` ne prend PAS de corps en second argument : il faut
  // passer par `{ data }`. Une signature à trois arguments enverrait un
  // DELETE sans corps, et le serveur refuserait toujours — un défaut qui se
  // présenterait comme « la confirmation ne marche jamais ».
  const response = await apiService.delete(`/admin/companies/${companyId}`, {
    data: { confirmation },
  });
  return response.data;
};

/**
 * Ce qu'une suppression détruirait — et ce qu'elle CONSERVERAIT.
 *
 * 🔴 L'ANNONCE VIENT DU SERVEUR. La recomposer à partir de `nb_stocks` et
 * `nb_users` de la liste donnerait deux chiffres sur neuf, et les sept autres
 * — produits, commandes, réceptions, paiements, invitations, fichiers, et le
 * POIDS de ces fichiers — resteraient invisibles. *Une annonce partielle est
 * plus trompeuse qu'une annonce absente : elle a l'air complète.*
 */
export const getImpactSuppression = async (companyId) => {
  const response = await apiService.get(
    `/admin/companies/${companyId}/impact-suppression`,
  );
  return response.data;
};

/**
 * Démarrer une impersonation : endosser l'identité de l'utilisateur `userId`.
 *
 * ⚠️ Route du blueprint `auth` (/api/auth/login-as-user/<id>), PAS de `companies`.
 * Elle est ici parce que seule la console super-admin l'appelle, mais son préfixe
 * diffère de toutes les autres méthodes de ce fichier.
 *
 * ⚠️ OPTION B (UI-0) — l'impersonation est BORNÉE À 30 MINUTES.
 * Le serveur pose un access cookie portant l'identité de la CIBLE, mais n'émet AUCUN
 * refresh token, et EFFACE celui du SUPER_ADMIN. Motif : sans blocklist de révocation,
 * un refresh de 30 jours portant le claim `impersonator_id` serait un credential
 * irrévocable, convertible en droits SA via /auth/impersonation/stop.
 * Conséquences directes :
 *   - à l'expiration de l'access, aucun refresh n'est possible -> déconnexion complète,
 *     le SA devra se reconnecter ;
 *   - POST /auth/impersonation/stop est le SEUL retour sans reconnexion.
 *
 * Réponse 200 : { user, stocks, impersonated: true, message, access_token,
 * refresh_token: null }. Les cookies font foi, le corps sert à peupler l'UI.
 * 400 si la cible est inactive ou introuvable, 403 si l'appelant n'est pas SUPER_ADMIN.
 */
export const impersonate = async (userId) => {
  const response = await apiService.post(`/auth/login-as-user/${userId}`);
  return response.data;
};

/**
 * Quitter une impersonation et retrouver son identité de SUPER_ADMIN.
 *
 * ⚠️ Route du blueprint `auth`, comme impersonate(). Elle est ici pour que l'aller et
 * le retour se lisent côte à côte — il n'existe pas d'authService dans ce projet, et en
 * créer un pour une seule fonction séparerait les deux moitiés d'un même mécanisme.
 *
 * ⚠️ APPELÉE DEPUIS UNE IDENTITÉ NON-SUPER_ADMIN. Le token courant porte celle de la
 * CIBLE (role='USER' le plus souvent) : la route n'a donc AUCUN garde sur le rôle
 * courant, volontairement. Son autorisation vient du claim `impersonator_id`, revérifié
 * en base côté serveur.
 *
 * Réponse 200 : session SA COMPLÈTE (access ET refresh reposés, contrairement à
 * l'aller), avec { user, stocks: [], impersonated: false }.
 *   400 — aucune impersonation en cours. Bénin : l'état local mentait, il suffit de le
 *         purger.
 *   403 — l'impersonateur n'est plus SUPER_ADMIN, ou est désactivé. L'utilisateur est
 *         COINCÉ dans l'identité cible : seule une déconnexion franche le sort de là.
 */
export const stopImpersonation = async () => {
  const response = await apiService.post('/auth/impersonation/stop');
  return response.data;
};

/**
 * Journal d'audit des impersonations (append-only, trié récent -> ancien).
 *
 * ⚠️ SEULE ROUTE PAGINÉE DE TOUT LE BLUEPRINT. Ne pas en déduire que les autres le
 * sont : GET /admin/companies renvoie TOUT, sans pagination ni tri paramétrable
 * (`order_by(Company.id)` en dur).
 * Paramètres : `page` (défaut 1) et `per_page` (défaut 20, PLAFONNÉ À 100 côté serveur —
 * demander davantage ne lève pas d'erreur, la valeur est silencieusement ramenée à 100).
 *
 * Réponse : { logs: [...], total, pages, current_page, per_page }.
 * Chaque entrée porte les champs bruts d'ImpersonationLog PLUS trois champs RÉSOLUS
 * côté serveur — `super_admin_email`, `target_user_email`, `target_company_name` —
 * qui valent `null` si l'entité a été supprimée depuis. Le front n'a donc aucune
 * jointure à faire.
 *
 * ⚠️ `ended_at` est TOUJOURS null : la colonne existe (66463af) mais rien ne l'écrit.
 * La renseigner suppose un `log_id` dans le claim d'impersonation, sans quoi /stop ne
 * sait pas quelle ligne clore. Chantier à part, noté au ROADMAP.
 */
export const getImpersonationLogs = async (params = {}) => {
  const response = await apiService.get('/admin/companies/impersonation-logs', {
    per_page: 100,
    ...params,
  });
  return response.data;
};

/**
 * Invite un administrateur dans une entreprise EXISTANTE.
 * L'entreprise doit avoir été créée au préalable : deux gestes séparés et
 * réversibles — si l'invitation échoue, l'entreprise reste et on réinvite.
 * Le jeton n'est JAMAIS renvoyé par l'API ; il ne part que par l'email.
 */
export const inviteCompanyAdmin = async (companyId, email) => {
  const response = await apiService.post(
    `/admin/companies/${companyId}/invitations`,
    { email },
  );
  return response.data;
};

/**
 * Désigne l'ADMIN d'une entreprise — **le seul chemin de transfert du produit.**
 *
 * 🔴 IL SERT DEUX CAS : le TRANSFERT (l'ancien ADMIN devient USER) et la
 * PROMOTION INITIALE (une société à zéro ADMIN en reçoit un). Les deux écritures
 * vivent dans la MÊME transaction côté serveur : *un invariant qui doit tenir à
 * tout instant ne se maintient pas en deux gestes.*
 *
 * ⚠️ AUCUN AUTRE ÉCRAN NE L'APPELLE, et c'est la règle métier : un ADMIN ne peut
 * ni se remplacer ni promouvoir quelqu'un. Le sélecteur de rôle a été retiré de
 * l'écran Admin au même lot.
 *
 * La réponse porte `ancien_admin` — l'ancien titulaire, ou `null` quand la
 * société n'en avait pas. *L'appelant distingue ainsi une promotion d'un
 * transfert sans avoir à le déduire.*
 */
export const designerAdmin = async (companyId, userId) => {
  const response = await apiService.put(
    `/admin/companies/${companyId}/admin`,
    { user_id: userId },
  );
  return response.data;
};
